/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import * as _ from 'underscore';
import { appConstants } from '@app/common/app-constants';
import { ListUpdate } from '@app/data-model/list-update';

export class CountUpdate extends ListUpdate {
  total: number;
  stat: number;
  findings: number;
  isUnmonitored = false;
  // this property can be used to un-monitor work-list count
  isUnmonitoredWhenInactive = false;
  checksum: number = null;

  constructor(
    countUpdate: any,
    canViewConfPat: boolean,
    accessibleInstitutes: any[],
    isInstituteSeparationEnabled = false
  ) {
    super(countUpdate.id.toString());
    const id = countUpdate.id.toString();
    const { total, stat, vipTotal, vipStat, findings, vipFindings, checksum } = CountUpdate.calculateCounts(
      countUpdate,
      accessibleInstitutes,
      isInstituteSeparationEnabled
    );

    this.id = id;
    this.setProperties(total, stat, vipTotal, vipStat, findings, vipFindings, canViewConfPat, checksum);
  }

  static calculateCounts(countUpdate: any, accessibleInstitutes: any[], isInstituteSeparationEnabled: boolean) {
    let total: number, stat: number, vipTotal: number, vipStat: number, findings: number, vipFindings: number;
    const checksum: number = countUpdate.eventId;
    const aiFindingCntUnavailable = _.isNull(countUpdate.totalAiFindingCnt);
    const aiFindingVipCntUnavailable = _.isNull(countUpdate.totalAiFindingVipCnt);

    if (isInstituteSeparationEnabled && countUpdate.byInstitute) {
      const accessibleInstituteIds = (accessibleInstitutes || []).map((inst: { id: any }) => inst.id);
      const sum = CountUpdate.sumInstitutes(countUpdate, accessibleInstituteIds);

      total = sum.totalItemsCnt;
      vipTotal = sum.totalVipItemsCnt;
      stat = sum.totalSTATCnt;
      vipStat = sum.totalVipSTATCnt;
      findings = aiFindingCntUnavailable ? -1 : sum.totalAiFindingCnt;
      vipFindings = aiFindingVipCntUnavailable ? -1 : sum.totalAiFindingVipCnt;
    } else {
      total = countUpdate.totalItemsCnt;
      vipTotal = countUpdate.totalVipItemsCnt;
      stat = countUpdate.totalSTATCnt;
      vipStat = countUpdate.totalVipSTATCnt;
      findings = aiFindingCntUnavailable ? -1 : countUpdate.totalAiFindingCnt;
      vipFindings = aiFindingVipCntUnavailable ? -1 : countUpdate.totalAiFindingVipCnt;
    }

    return { total, stat, vipTotal, vipStat, findings, vipFindings, checksum };
  }

  static sumInstitutes(countUpdate: any, accessibleInstituteIds: string[]) {
    const sum = {
      totalItemsCnt: 0,
      totalVipItemsCnt: 0,
      totalSTATCnt: 0,
      totalVipSTATCnt: 0,
      totalAiFindingCnt: 0,
      totalAiFindingVipCnt: 0
    };
    accessibleInstituteIds.forEach((instId: string) => {
      const instData = countUpdate.byInstitute?.[String(instId)];
      if (instData) {
        sum.totalItemsCnt += instData.totalItemsCnt || 0;
        sum.totalVipItemsCnt += instData.totalVipItemsCnt || 0;
        sum.totalSTATCnt += instData.totalSTATCnt || 0;
        sum.totalVipSTATCnt += instData.totalVipSTATCnt || 0;
        if (!_.isNull(instData.totalAiFindingCnt)) {
          sum.totalAiFindingCnt += instData.totalAiFindingCnt;
        }
        if (!_.isNull(instData.totalAiFindingVipCnt)) {
          sum.totalAiFindingVipCnt += instData.totalAiFindingVipCnt;
        }
      }
    });
    return sum;
  }

  setProperties(total, stat, vipTotal, vipStat, findings, vipFindings, canViewConfPat, checksum) {
    // Count update
    if (total >= 0 && total !== null) {
      if (!canViewConfPat && !_.contains(appConstants.vipExcludedWorkList, this.id) && total > 0) {
        total = vipTotal > 0 ? total - vipTotal : total;
        stat = total > 0 && vipStat > 0 ? stat - vipStat : total > 0 ? stat : 0;
        findings = total > 0 && vipFindings > 0 ? findings - vipFindings : total > 0 ? findings : 0;
      }

      this.total = total;
      this.stat = stat;
      this.findings = findings;
    } else {
      this.isUnmonitored = true;
    }

    // Checksum update
    // eventId = null ==> Count update only, no checksum status
    // eventId = 1    ==> Checksum success status
    // eventId = -1   ==> Checksum error status OR
    //                    Count calculation error status without any checksum change
    // eventId = -2   ==> Count calculation error status - with checksum change
    this.checksum = checksum;
  }
}
